import { DealerID, DMS_QUOTA_STATUS } from "./Dealer";
import { DealerLocation, DealerLocationID } from "./DealerLocation";
import { User, UserID } from "./User";

export type DealerDMSID = number;

export type DealerDMSTime = string;

export type DealerLocationDMSID = number;

export type DMSCustomQueryHistoryID = number;

export enum DMSIDS {
  UNKNOWN = 1,
  NTCAR = 2,
  EVA = 3,
  WINCAR = 4,
  ANON_EVA = 5,
  AUTOLINE = 6,
  AUTOLINE_EF_NEFKENS = 7,
  TEST_API = 8,
  TEST_SOW = 9,
  INCADEA = 10,
  TRUCK_VISION = 11,
  ICAR_WO = 12,
  CARIT = 13,
  AUTOLINE_OPEL_MENU = 14,
  KEYLOOP_MENUS = 15,
  CUSTOM_QUERIES = 16,
  KEYLOOP_JOBS = 17,
  EVA_BE_WO = 18,
  ICAR_PLANNING = 19,
  AUTOFLEX = 20,
  AUTOFLEX_QA_STAGING = 21,
  NEXTLANE = 22,
  WINCAR_V1_WB = 23,
  EVA_BE = 24,
  EVA_FR = 25,
  INCADEA_API = 26,
  WINCAR_V2_WB = 27,
  WINCAR_API_EVENTS = 29,
  WINCAR_API_V2_EVENTS = 30,
  KEYLOOP_MENUS_STAGING = 31,
  KEYLOOP_JOBS_STAGING = 32,
  CARIT_HYBRID = 33,
  INCADEA_HYBRID = 34,
  VINACLES = 35,
  TURBO_SAP = 36,
  GRAND_PRIX_PUBLIC_API = 37,
  CARSYS_PUBLIC_API = 38
}

export enum DMS_CAPABILITIES {
  CarInShop = 1,
  ScheduledInOut = 2,
  CancelAppointment = 3,
  ImportMileage = 4,
  WriteBackAPKDate = 5,
  AppointmentSearch = 6,
  WriteBackCarData = 7,
  WriteBackCustomerData = 8,
  Import = 9,
  Refresh = 10,
  Create = 11,
  CustomerSearch = 12,
  CreateInterventionElement = 13,
  UpdateInterventionElement = 14,
  DeleteInterventionElement = 15,
  BulkImporter = 16,
  WriteBackCarOutOfShop = 17,
  UpdateScheduling = 18,
  CarSearch = 19,
  CarCustomerSearch = 20,
  WriteBackMileage = 21,
  WriteBackCarInShop = 22,
  AutomaticAppointmentRefresh = 23,
  CreateIntervention = 24,
  UpdateIntervention = 25,
  DeleteIntervention = 26,
  InterventionElementPartStatus = 27,
  FetchVersion = 141
}

export enum DMS_CAPABILITY_SUPPORTED_STATUSES {
  Supported = 1,
  Unsupported = 2,
  Unknown = 3
}

export enum DMS_CUSTOM_QUERY_HISTORY_STATUSES {
  Pending = 1,
  Executed = 2,
  Error = 3
}

export interface DealerDMS {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  dealer_id: DealerID; // Dealer Identifier
  dms_id: DMSIDS; // DMS platform Identifier
  dmsv3_key: string; // Key used to authenticate requests
  dsn: string; // DSN given to the client so it can connect to the server
  time_to_sleep: string; // The minimum interval between requests
  date_range: number; // Total amount of days in the future to import
  next_call_at: string | null; // Estimated time at which requests to the client would occur
  duration_threshold: string; // Estimated time for all requests to complete for a given interval
  start_time: DealerDMSTime; // Requests to the client will start until it reaches end_time
  end_time: DealerDMSTime; // Requests to the client will end until it reaches start_time
  is_cron: boolean; // The client is expected to be managed as a server cron task
  is_custom_query_only: boolean; // Client will only be running custom queries, no appointments, cars, or customers, will be imported
  last_activation: string; // Datetime when the interface was activated for importing appointments
  import_date_created: boolean; // Import the creation date of the appointment, disable if the DMS restart the WO numbers
  is_interval_full_run_enabled: boolean; // Full Run is enabled
  full_run_hour_interval: number; // How often to perform a full run in hours
  last_full_run: string; // Time at which the last full run occurred
  delay: string; // Time between each request to the client in milliseconds
  socket_read_timeout: number; // Time to wait before killing a websocket when reading
  dms_version: string; // Current version of the DMS API
  version_last_change: string | null; // Last time the DMS version changed
  dealer_locations?: DealerLocation[];
  active?: boolean;
  dealer_name?: string;
  dms_quota_warning_reached?: DMS_QUOTA_STATUS;
  icar_config?: DealerDMSICarConfig;
  incadea_config?: DealerDMSIncadeaConfig;
  keyloop_config?: DealerDMSKeyloopConfig;
  autoline_config?: DealerDMSAutolineConfig;
  eva_config?: DealerDMSEvaConfig;
  wincar_config?: DealerDMSWinCarConfig;
  carit_config?: DealerDMSCarITConfig;
  nextlane_config?: DealerDMSNextlaneConfig;
  test_api_config?: DealerDMSTestAPIConfig;
  vinacles_config?: DealerDMSVinaclesConfig;
}

export interface DealerLocationDMS {
  created_on: string;
  updated_on: string;
  dealer_location_dms_id: DealerLocationDMSID;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  dealer_location_id: DealerLocationID; // Dealer Location Identifier
  comment: string; // Comments for this configuration
  dms_location_id: string; // Location Identifier in the DMS
  dms_database: string; // Database Identifier in the DMS
  maintenance_intervention: string; // Identify interventions to be labeled as Maintenance when importing from the DMS
  customer_not_approved: string; // Identify interventions that are not customer approved when importing from the DMS
  not_visible_to_customer: string; // Identify interventions that are not visible to the customer when importing from the DMS
  exclude_from_import: string; // Identify interventions to be not imported when importing from the DMS
  last_event_on: string; // Timestamp of last processed event, if the DMS supports it
  autoline_config?: DealerLocationDMSAutolineConfig;
  icar_config?: DealerLocationDMSICarConfig;
  incadea_config?: DealerLocationDMSIncadeaConfig;
  truckvision_config?: DealerLocationDMSTruckVisionConfig;
  keyloop_config?: DealerLocationDMSKeyloopConfig;
}

export interface DMSCapability {
  dms_id: DMSIDS;
  dms_capability_id: DMS_CAPABILITIES;
}

export interface DMSCustomQueryHistory {
  created_on: string;
  updated_on: string;
  id: DMSCustomQueryHistoryID;
  name: string; // Name of the query
  statement: string; // Statement representing the query
  result: string | null; // Result of the query
  dms_id: DMSIDS; // DMS who is performing the execution
  user_id: UserID; // User who performed this query
  dealer_location_id: DealerLocationID; // DealerLocation from where the query was created
  status: DMS_CUSTOM_QUERY_HISTORY_STATUSES; // Status of the query execution
  estimated_execution: string; // Estimation of when this query will be executed
  executed_at: string | null; // When the query was executed
  execution_duration: string; // How long the query took to execute
  user?: User;
}

export interface DealerDMSICarConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  dms_dealer_id: string;
  dms_customer_waiting_id: string; // Identifier for a customer waiting in the dms
}

export interface DealerDMSIncadeaConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  dms_dealer_id: string;
  dms_appointment_type: string; // Appointment type in the DMS to query for some extra data like APK
  dms_warranty_intervention_id: string; // Identify intervention that are for warranty purpose
  dms_internal_intervention_id: string; // Identify intervention that are internal
  username: string; // API Username
  password: string; // API Password
  api_base_url: string; // API base URL
}

export interface DealerDMSKeyloopConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  contract_code: string; // Dealer identifier for Keyloop
  api_key: string; // Optional overrule for keyloop api key
  api_password: string; // Optional overrule for keyloop api password
}

export interface DealerDMSAutolineConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  business_customer_table_id: string; // Table to use when selecting business customers (contractors)
  customer_table_id: string; // Table to use when selecting customers
  car_table_id: string; // Table to use when selecting cars
  customer_detail_table_id: string; // Table to use when selecting customer details
  car_make_table_id: string; // Table to use when selecting car make
  car_note_table_id: string; // Table to use when selecting car notes
  department_id: string; // Department Identifier, since Locations can be split into departments
  vat_table_id: string; // Table to use when selecting how to calculate VAT on prices
  price_includes_vat: string; // Prices include VAT already calculated
  rental_billed: string; // Identifier for the Customer having paid for using a rental vehicle, used to set the billed status
  rental_code: string; // Identifier for the code of the rental vehicle, used to set the billed status
  rental_status: string; // Identifier for the state of the rental vehicle, used to set the billed status
}

export interface DealerDMSEvaConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  dms_lease_customer_ids: string; // Identifier for lease customer identifiers in the dms
  dms_lease_customer_category_ids: string; // Identifier for lease customer identifiers in the dms
}

export interface DealerDMSWinCarConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  dms_lease_customer_ids: string; // Identifier for lease customer identifiers in the dms
  client_id: string; // Client ID used in authenticating with API
  audience: string; // Audience used in authenticating with API
  client_secret: string; // Client secret used in authenticating with API
  api_base_url: string; // Base URL for the WinCar service API
  internal_revenue_kind: string; // Revenue kinds to be marked as internal
  bulk_import_enabled: boolean; // Enable bulk importing strategy instead of single appointment fetching
  auth_token: string | null; // Currently used auth token
}

export interface DealerDMSCarITConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  import_offers: boolean; // Import WO's that are internal offers from CarIT
  internal_orders: boolean; // Import WO's that internal or external orders from CarIT
  internal_customer_group_ids: string; // Identifier for internal customer groups in the dms
  api_base_url: string; // The base url for CarIT api, every dealer has a unique url
  user_name: string; // The UserName of Basic Authentication
  password: string; // The Password of Basic Authentication
}

export interface DealerDMSNextlaneConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  dealership_id: string; // Dealership identifier
  api_key: string; // API key to connect to Nextlane API with
}

export interface DealerDMSTestAPIConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  api_key: string; // API key to connect to Claire API with
}

export interface DealerDMSVinaclesConfig {
  created_on: string;
  updated_on: string;
  dealer_dms_id: DealerDMSID; // Dealer DMS Identifier
  client_id: string; // Client ID used in authenticating with API
  audience: string; // Audience used in authenticating with API
  client_secret: string; // Client secret used in authenticating with API
  api_base_url: string; // Base URL for the Vinacles service API
}

export interface DealerLocationDMSIncadeaConfig {
  created_on: string;
  updated_on: string;
  dealer_location_dms_id: DealerLocationDMSID; // Dealer location dms Identifier
  dms_dealer_id: string;
  dms_appointment_type: string; // Appointment type in the DMS to query for some extra data like APK
  dms_warranty_intervention_id: string; // Identify intervention that are for warranty purpose
  dms_internal_intervention_id: string; // Identify intervention that are internal
}

export interface DealerLocationDMSICarConfig {
  created_on: string;
  updated_on: string;
  dealer_location_dms_id: DealerLocationDMSID; // Dealer location dms Identifier
  dms_dealer_id: string; // Dealer identifier in the DMS, per-location override of the dealer-level DMS Dealer ID
}

export interface DealerLocationDMSAutolineConfig {
  created_on: string;
  updated_on: string;
  dealer_location_dms_id: DealerLocationDMSID; // Dealer location dms Identifier
  business_customer_table_id: string; // Table to use when selecting business customers (contractors)
  customer_table_id: string; // Table to use when selecting customers
  car_table_id: string; // Table to use when selecting cars
  customer_detail_table_id: string; // Table to use when selecting customer details
  car_make_table_id: string; // Table to use when selecting car make
  car_note_table_id: string; // Table to use when selecting car notes
  department_id: string; // Department Identifier, since Locations can be split into departments
  vat_table_id: string; // Table to use when selecting how to calculate VAT on prices
  price_includes_vat: string; // Prices include VAT already calculated
  rental_billed: string; // Identifier for the Customer having paid for using a rental vehicle, used to set the billed status
  rental_code: string; // Identifier for the code of the rental vehicle, used to set the billed status
  rental_status: string; // Identifier for the state of the rental vehicle, used to set the billed status
}

export interface DealerLocationDMSTruckVisionConfig {
  created_on: string;
  updated_on: string;
  dealer_location_dms_id: DealerLocationDMSID; // Dealer location dms Identifier
  warranty_process_ids: string; // ProcessIDs for warranty interventions
  internal_process_ids: string; // ProcessIDs for internal interventions
}

export interface DealerLocationDMSKeyloopConfig {
  created_on: string;
  updated_on: string;
  dealer_location_dms_id: DealerLocationDMSID; // Dealer location dms Identifier
  business_unit: string; // business unit for keyloop locations
}
